import React from 'react';
import {
  call, takeLatest, put, select,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import { SitesActionTypes } from './sites.types';
import sitesActions from './sites.actions';
import {
  AWS_GATOR_SOLUTIONS_URL, CACHED_AWS_GATOR_SOLUTIONS_URL, COUNTRY, config, WHMCS_URL,
  AGENT_FACTORY_ORCHESTRATOR_API_URL,
} from '@/config';
import { authActions } from '@/redux/modules/auth';
import sitesAssembly from './sites.assembly';
import { commonActions } from '@/redux/modules/common';
import { locale } from '@/utils/locale';
import {
  identify as identifyAmplitude, Identify as IdentifyObject,
} from '@amplitude/analytics-browser';
import { isBRServer } from '@/utils/Validators/validation';
import { AUTO_SSL_STATUS } from '@/enums/sites/status.enum';
import logger from '@/utils/logger';
import { linkNaBioActions } from '../linkNaBio';
import {
  ATIVACA_MIGRATE_WP_ATTEMPTS, ATIVACAO_CHANGEDOMAIN_FAILED_WHOIS, ATIVACAO_MIGRATE_WP_FILE_MB_SIZE, ATIVACAO_REPLACE_WHOIS_WITH_CHECK_DOMAIN_AVAILABLE,
} from '@/config/GrowthBook/constants';
import { getCookie } from '@/utils/Application/cookies';
import { create as createAgent, activate as activateAgent, installAiAgent } from '../agentsFactory/agentsFactory.sagas';

export function* requestBuilderSso({ payload }) {
  const {
    builderAddonId, domain, callback, popupBlockedAction, isAgentFactory,
  } = payload;

  const { token, jwt: jwtToken } = yield select(state => state.auth);

  yield put(sitesActions.builderSso.loading(true));

  try {
    const request = {
      url: 'api/v3/builder/sso',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Service-Token': jwtToken,
      },
      data: {
        builder_addon_id: builderAddonId,
        domain,
      },
    };

    const response = yield call(api.whmcs.post, request);

    const ssoUrl = response.data && response.data.url;
    yield put(sitesActions.builderSso.set({ builderAddonId, domain, url: ssoUrl }));

    if (!isAgentFactory) {
      const newWindow = window.open(ssoUrl, '_blank');

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        popupBlockedAction && popupBlockedAction();
      }
    }

    callback && callback(ssoUrl);
    yield put(sitesActions.builderSso.success());
  } catch (e) {
    yield put(sitesActions.builderSso.failure());
    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
  yield put(sitesActions.builderSso.loading(false));
}

export function* getSitesRequest() {
  const { jwt: jwtToken, token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';


  const request = {
    url: `${CACHED_AWS_GATOR_SOLUTIONS_URL}/hostings/domains`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  yield put(sitesActions.loadingSites.set(true));

  try {
    const response = yield call(api.aws.get, request);

    const sitesData = sitesAssembly.receiveSitesFromGatorSolutions(response);

    if (sitesData) {
      const identifyObject = new IdentifyObject();
      identifyObject.set('sites_amount', sitesData.length);
      identifyAmplitude(identifyObject);
    }

    yield put(sitesActions.sites.set(sitesData));
    yield put(sitesActions.sites.success());
    yield put(sitesActions.loadingSites.set(false));
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas getSitesRequest', error);
    yield put(sitesActions.sites.failure());
  }
  yield put(sitesActions.loadingSites.set(false));
}

function* redirectToEdit(action) {
  const { jwt: jwtToken, token } = yield select(state => state.auth);
  const {
    builderAddonId,
    popupBlockedAction,
    hostingId,
    domain,
    history,
    siteBuilderUrl,
    sitesWizardUrl,
    webPresenceUrl,
    cpanelUrl,
  } = action.payload;

  const brand = isBRServer ? 'br' : 'es';


  const request = {
    url: `${CACHED_AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/builder_tools/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  yield put(sitesActions.site.loadRedirect(domain));
  try {
    const response = yield call(api.aws.get, request);

    const rules = {
      site_builder_unpublished: () => window.open(siteBuilderUrl(domain)),
      site_builder: () => window.open(siteBuilderUrl(domain)),
      empty: () => history.push(`${sitesWizardUrl}`),
      webpresence: () => window.open(`${webPresenceUrl}${hostingId}`),
      undentified: () => cpanelUrl({ hostingId }),
    };

    const isInToolsArray = [
      'site_builder_unpublished',
      'site_builder',
      'empty',
      'webpresence',
      'undentified',
    ].includes(response.data.tool);
    const isWordpress = response.data.tool.toLowerCase().includes('wordpress');
    const isHGBuilder = response.data.tool === 'hg_builder';

    if (isHGBuilder) {
      if (builderAddonId) {
        yield call(requestBuilderSso, { payload: { builderAddonId, domain, popupBlockedAction } });
      }
      yield put(sitesActions.site.loadRedirect(false));
      return;
    }

    if (isInToolsArray) {
      rules[response.data.tool]();
    }

    if (isWordpress) {
      window.open(`https://${domain}/wp-admin`);
    }

    if (!isInToolsArray && !isWordpress && response.data.tool) {
      cpanelUrl({ hostingId, softaculous: true });
    }
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas redirectToEdit', error);
  }
  yield put(sitesActions.site.loadRedirect(false));
}

function* getSiteBuilderTools(action) {
  const { jwt: jwtToken, token } = yield select(state => state.auth);
  const {
    hostingId,
    domain,
    callback,
  } = action.payload;

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${CACHED_AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/builder_tools/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.aws.get, request);

    if (callback) {
      callback(response.data);
    }
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas getSiteBuilderTools', error);
  }
}

function* requestSiteBuilderTools(action) {
  const { jwt: jwtToken, token } = yield select(state => state.auth);

  const {
    hostingId,
    domain,
    redirect,
  } = action.payload;

  const brand = isBRServer ? 'br' : 'es';


  const request = {
    url: `${CACHED_AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/builder_tools/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  yield put(sitesActions.domainBuilderTools.loading(true));
  try {
    const response = yield call(api.aws.get, request);
    yield put(sitesActions.domainBuilderTools.set(response.data));

    const blockedTools = ['site_builder', 'site_builder_unpublished'];
    const isRedirectBlocked = blockedTools.includes(response.data.tool);

    if (!!redirect && !isRedirectBlocked) {
      redirect();
    }
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas requestSiteBuilderTools', error);
  }

  yield put(sitesActions.domainBuilderTools.loading(false));
}

function* deleteSite(action) {
  const { site, setSiteInExclusion } = action.payload;

  const { token } = yield select(state => state.auth);
  const { id: userId, builderEmail } = yield select(state => state.summary);

  yield put(sitesActions.loadingExclusion.set(true));

  const request = {
    url: `${config.AWS_SITEBUILDER_URL}/sitebuilder/${builderEmail}/${site.site}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': userId,
      'X-Brand': COUNTRY,
    },
  };

  try {
    yield call(api.aws.delete, request);

    yield put(commonActions.notifications.set({
      label: (<p>{locale('sites.exclusionToast.success')}</p>),
      type: 'success',
    }));
    yield put(sitesActions.sites.request());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas deleteSite', error);
    yield put(commonActions.notifications.set({
      label: (<p>{locale('sites.exclusionToast.failure')}</p>),
      type: 'error',
    }));
  }
  setSiteInExclusion(false);
  yield put(sitesActions.loadingExclusion.set(false));
}

export function* getDomainsRequest(action) {
  const { getSitesWithBuilderTools } = action.payload;

  const { jwt: jwtToken } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);

  yield put(sitesActions.loadingDomains.set(true));
  try {
    const request = {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'X-Client-ID': userId,
        'X-Brand': COUNTRY,
      },
      url: `/clients/domains${getSitesWithBuilderTools ? '?bringDomainsWithBuilderTools=true' : ''}`,
    };

    const response = yield call(api.aws.get, request);
    const domainsData = sitesAssembly.receiveDomains(response);

    yield put(sitesActions.domains.set(domainsData));
    yield put(sitesActions.domains.success());
  } catch (e) {
    yield put(sitesActions.domains.failure());
  }
  yield put(sitesActions.loadingDomains.set(false));
}

export function* getTlds() {
  yield put(sitesActions.loadingTlds.set(true));
  try {
    const request = {
      url: '/api/v3/tld',
      headers: {
        brand: COUNTRY.toUpperCase(),
      },
    };

    const response = yield call(api.whmcs.get, request);

    const tlds = sitesAssembly.receiveTlds(response);

    yield put(sitesActions.tlds.set(tlds));
    yield put(sitesActions.tlds.success());
  } catch (e) {
    yield put(sitesActions.tlds.failure());
  }
  yield put(sitesActions.loadingTlds.set(false));
}

export function* getDomainAvailability(action) {
  const { token } = yield select(state => state.auth);
  const { domain } = action.payload;

  yield put(sitesActions.loadingDomainValidation.set(true));
  try {
    const request = {
      url: `${config.API_URL}/clientarea/br/check-domain-available/${domain}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const response = yield call(api.whmcs.get, request);

    const domainAvailability = sitesAssembly.receiveDomainAvailability(response);

    yield put(sitesActions.domainAvailability.set({ ...domainAvailability, domainAndSubdomain: domain }));
    yield put(sitesActions.domainAvailability.success());
    yield put(sitesActions.loadingDomainValidation.set(false));
  } catch (e) {
    yield put(sitesActions.domainAvailability.failure());
    if (e.response && e.response.status === 401) {
      yield put(authActions.tokens.jwt.errorDuringRefresh(true));
    }
    yield put(sitesActions.loadingDomainValidation.set(false));
  }
}

export function* getPlans() {
  const { jwt: jwtToken, token } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);
  const brand = isBRServer ? 'br' : 'es';
  const endpoint = '/plans';

  yield put(sitesActions.loadingPlans.set(true));

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': userId,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
    url: CACHED_AWS_GATOR_SOLUTIONS_URL + endpoint,
  };

  try {
    let response = null;

    response = yield call(api.aws.get, request);

    const plansData = sitesAssembly.receivePlans(response);

    yield put(sitesActions.plans.set(plansData));
    yield put(sitesActions.plans.success());
  } catch (e) {
    yield put(sitesActions.plans.failure());
  }
  yield put(sitesActions.loadingPlans.set(false));
}

export function* setWarningSeen() {
  const { jwt: jwtToken } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);

  const request = {
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      'X-Client-ID': userId,
      'X-Brand': COUNTRY,
    },
    url: '/sites/warning',
  };

  try {
    yield call(api.aws.post, request);
    yield put(sitesActions.warnings.success());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas setWarningSeen', error);
    yield put(sitesActions.warnings.failure());
  }
}

export function* linkDomainToPlan(action) {
  const { domain, planExternalId } = action.payload;
  const { jwt: jwtToken, token } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';
  const url = `api/v2/clientarea/${brand}/user/hostings/domain`;
  yield put(sitesActions.loading.set(true));

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-service-token': jwtToken,
    },
    url,
    data: {
      domain,
      external_id: planExternalId,
    },
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(sitesActions.aditionalDomain.success());
    yield put(sitesActions.aditionalDomain.set({ domainLinkedToPlan: true }));
    yield put(sitesActions.loading.set(false));
    return true;
  } catch (error) {
    const isBadRequest = error && error.response && error.response.status === 400
      && error.response.data.data === 'Bad Request';

    if (isBadRequest) {
      yield put(sitesActions.aditionalDomain.success());
      yield put(sitesActions.aditionalDomain.set({ domainLinkedToPlan: true }));
      yield put(sitesActions.loading.set(false));
      return true;
    }

    if (!isBadRequest) {
      yield put(sitesActions.loading.set(false));
      yield put(sitesActions.aditionalDomain.set({ domainLinkedToPlan: false }));
      if (error && error.message && error.message.includes(`A DNS entry for the domain “${domain}.cloudintegrationlatam.com” already exists`)) {
        yield put(sitesActions.aditionalDomain.success());
      } else {
        yield put(sitesActions.aditionalDomain.failure());
      }
      return false;
    }
  }
}

export function* changeDomainDns(action) {
  const { domainId, planId, selectedDomain } = action.payload;

  const { token } = yield select(state => state.auth);
  const request = {
    headers: {
      Authorization: token,
    },
    url: `api/v2/clientarea/br/dns/domain/${domainId}`,
    data: { hostingId: planId },
  };

  try {
    const response = yield call(api.whmcs.put, request);
    const domainDnsChanged = response.data.data.message === 'success';
    yield put(sitesActions.changeDomainDns.set({ domainDnsChanged }));
    yield put(sitesActions.changeDomainDns.success());
  } catch (e) {
    yield put(sitesActions.changeDomainDns.failure());
    const error = { ...e, userRequestData: request, selectedDomain };
    logger.error('Error sagas changeDomainDns', error);
  }
}

export function* getDomainDns({ payload }) {
  const { AWS_DNS_SERVICE_URL } = config;
  const {
    domainLabel, onDomainRegitered, onDomainNotRegistered,
  } = payload;

  const { token, jwtToken } = yield select(state => state.auth);
  yield put(sitesActions.domainDns.resetStatus());
  yield put(sitesActions.domainDns.loading(true));
  yield put(sitesActions.loading.set(true));


  const commonReducer = yield select(state => state.common);

  const canRequestCheckDomainAvailable = commonReducer && commonReducer.toggles[ATIVACAO_REPLACE_WHOIS_WITH_CHECK_DOMAIN_AVAILABLE];
  const shouldIgnoreFailure = commonReducer && commonReducer.toggles[ATIVACAO_CHANGEDOMAIN_FAILED_WHOIS];

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    url: `${config.API_URL}/clientarea/br/check-domain-available/${domainLabel}`,
  };

  if (!canRequestCheckDomainAvailable) {
    request.url = `${AWS_DNS_SERVICE_URL}/domains/${domainLabel}/whois`;
    request.headers.Authorization = `Bearer ${jwtToken}`;
  }

  try {
    const response = yield call(api.aws.get, request);

    let list;
    let domainIsAvailable;

    if (canRequestCheckDomainAvailable) {
      domainIsAvailable = response.data.data.available;
      list = response.data.data;
      list.name_servers = list.nameservers;
    }

    if (!canRequestCheckDomainAvailable) {
      domainIsAvailable = !response.data.domain.id;
      list = response.data;
      list.name_servers = list.domain.name_servers;
    }

    const domainDns = sitesAssembly.receiveDomainDns(list);
    yield put(sitesActions.domainDns.set(domainDns));
    yield put(sitesActions.domainDns.success());
    yield put(linkNaBioActions.domainVerify.success());

    if (domainIsAvailable) {
      onDomainNotRegistered && onDomainNotRegistered();
    }

    if (!domainIsAvailable) {
      onDomainRegitered && onDomainRegitered();
    }
  } catch (e) {
    if (shouldIgnoreFailure) {
      yield put(sitesActions.domainDns.set(['ignore']));
      yield put(sitesActions.domainDns.success());
      yield put(linkNaBioActions.domainVerify.success());
      onDomainRegitered && onDomainRegitered();
    }

    if (!shouldIgnoreFailure) {
      yield put(sitesActions.domainDns.failure());
      onDomainNotRegistered && onDomainNotRegistered();
      yield put(linkNaBioActions.domainVerify.failure());
    }
  }
  yield put(sitesActions.loading.set(false));
  yield put(sitesActions.domainDns.loading(false));
}

export function* getPlanDns({ payload }) {
  const { planId } = payload;

  const { token } = yield select(state => state.auth);

  yield put(sitesActions.loading.set(true));
  yield put(sitesActions.planDns.loading(true));
  try {
    const request = {
      headers: {
        Authorization: token,
      },
      url: `api/v2/clientarea/${COUNTRY}/product/hosting/${planId}`,
    };
    const response = yield call(api.whmcs.get, request);
    const hasSiteBuilderActive = sitesAssembly.receiveHasSiteBuilderActive(response.data);
    const planDns = sitesAssembly.receivePlanDns(response.data);

    yield put(sitesActions.hasSiteBuilderActive.set(hasSiteBuilderActive));
    yield put(sitesActions.planDns.set(planDns));
    yield put(sitesActions.planDns.success());
  } catch (e) {
    yield put(sitesActions.planDns.failure());
  }
  yield put(sitesActions.planDns.loading(false));
  yield put(sitesActions.loading.set(false));
}

export function* requestWordPressInstallation({ payload }) {
  yield put(sitesActions.wordpressInstallation.set(true));
  const { jwt: jwtToken, token } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);
  const {
    wordpress,
    setHgPluginIsInstall,
    isWondersuiteEnabled,
    themeId,
    userSelectedWondersuiteTrial,
    isFastCheckoutAddonEnabled,
    userSelectedUseEcommercePlus,
    requestAutoSsl,
  } = payload;

  const defaultAddonId = '14';
  const defaultPaymentMethod = 'braspag_credit_1x';
  const defaultPaymentCycle = 'monthly';

  const data = {
    customer_id: userId,
    softdomain: wordpress.domain,
    softsubmit: 1,
    admin_username: wordpress.admin,
    admin_pass: wordpress.password,
    admin_email: wordpress.email,
    site_name: wordpress.name,
    site_desc: wordpress.description,
    theme_id: themeId,
    sets_name: wordpress.sets,
    external_id: wordpress.external_id,
  };

  const wpType = userSelectedWondersuiteTrial || userSelectedUseEcommercePlus ? 'basic' : '';

  if (isWondersuiteEnabled) {
    data.wp_type = wpType;
  }

  const requestPayload = isFastCheckoutAddonEnabled ? {
    wp_install: data,
  } : data;

  isFastCheckoutAddonEnabled && (userSelectedWondersuiteTrial && !userSelectedUseEcommercePlus) && (requestPayload.addon = {
    products: [],
    service_id: wordpress.external_id.toString(),
    addon_id: defaultAddonId,
    payment_method: defaultPaymentMethod,
    cycle: defaultPaymentCycle,
    offer_contexts: ['criar_site_trial_woopremium_activation'],
    is_trial: true,
  });

  const headers = isWondersuiteEnabled ? { Authorization: token } : {
    Authorization: `Bearer ${jwtToken}`,
    'X-Client-ID': userId,
    'X-Brand': COUNTRY,
  };

  const { domain, external_id: hostingId } = wordpress;

  const url = isWondersuiteEnabled
    ? `api/v2/clientarea/${COUNTRY}/user/hostings/${hostingId}/domains/${domain}/site/install`
    : `${config.AWS_PRODUCT_LIFECYCLE_URL}/wordpress/install`;

  try {
    const request = {
      headers,
      data: requestPayload,
      url,
    };
    const response = isWondersuiteEnabled ? yield call(api.whmcs.post, request) : yield call(api.aws.post, request);
    const hgPluginInstalled = sitesAssembly.hgPluginInstalled(response);

    yield put(sitesActions.wordpressInstallation.success({
      ssoUrl: response.data.data.ssoUrl,
      siteId: response.data.data.siteId,
    }));
    yield put(sitesActions.wordpressInstallation.setStatus(true));

    if (hgPluginInstalled) {
      setHgPluginIsInstall && setHgPluginIsInstall(true);
    } else {
      setHgPluginIsInstall && setHgPluginIsInstall(false);
    }
    requestAutoSsl && requestAutoSsl();
  } catch (e) {
    yield put(sitesActions.wordpressInstallation.failure());
    yield put(sitesActions.wordpressInstallation.setStatus(false));
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('sitesWizard.wordPressInstall.errorToast')}`}
        </p>
      ),
      type: 'error',
    }));
  }

  yield put(sitesActions.wordpressInstallation.set(false));
}

export function* requestWordpressFtpInstallation({ payload }) {
  const { token } = yield select(state => state.auth);
  const { builderId, wizard } = yield select(state => state.newOnboarding);

  const {
    formData, hostingId, domain,
  } = payload;

  const data = {
    builder_id: builderId || wizard.id,
    domain: formData.sourceDomain,
    softdomain: domain,
    server_host: formData.sourceIp,
    ftp_user: formData.userName,
    ftp_pass: formData.password,
    ftp_path: formData.sourcePath,
    installed_path: '',
    dest_directory: formData.destinationPath,
    softdb: formData.databaseName,
    protocol: 'ftp',
    port: formData.sourcePort || '21',
  };

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
    url: `api/v2/clientarea/${COUNTRY}/user/hostings/${hostingId}/wordpress/import/remote`,
  };

  yield put(sitesActions.wordpressFtpMigration.loading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(sitesActions.wordpressFtpMigration.success());
    return response;
  } catch (error) {
    logger.error('Error on migrate wordpress via ftp', error);
    yield put(sitesActions.wordpressFtpMigration.failure());
  }
  yield put(sitesActions.wordpressFtpMigration.loading(false));
}

export function* requestWpMigrationFileData({ payload }) {
  const {
    hostingId, domain, files,
  } = payload;
  const { token } = yield select(state => state.auth);
  const { toggles } = yield select(state => state.common);
  const { builderId, wizard } = yield select(state => state.newOnboarding);

  const baseChunkSize = toggles[ATIVACAO_MIGRATE_WP_FILE_MB_SIZE] && toggles[ATIVACAO_MIGRATE_WP_FILE_MB_SIZE].size ? toggles[ATIVACAO_MIGRATE_WP_FILE_MB_SIZE].size : 500;
  const baseAttempts = toggles[ATIVACA_MIGRATE_WP_ATTEMPTS] && toggles[ATIVACA_MIGRATE_WP_ATTEMPTS].attempts ? toggles[ATIVACA_MIGRATE_WP_ATTEMPTS].attempts : 3;

  const baseChunkSizeToMb = baseChunkSize * 1024 * 1024;

  const data = {
    domain,
    builder_id: builderId || wizard.id,
  };

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data,
    url: `api/v2/clientarea/${COUNTRY}/user/hostings/${hostingId}/wordpress/import/file`,
  };

  yield put(sitesActions.wordpressFileMigration.loading(true));
  try {
    const response = yield call(api.whmcs.post, request);

    if (response.data.data) {
      const migrationId = response.data.data.id;
      const { server } = response.data.data;
      const {
        session, path, id: serverId, hostname,
        token: migrationToken,
        username,
      } = server;

      const chunkSize = baseChunkSizeToMb;

      const chunksPerFile = files.map((file) => {
        const chunks = [];

        if (file.size <= chunkSize) {
          chunks.push(file);
        }

        if (file.size > chunkSize) {
          let start = 0;
          let chunkIndex = 0;

          while (start < file.size) {
            const end = Math.min(start + chunkSize, file.size);
            const chunk = file.slice(start, end);
            chunk.name = `${file.name}.part${(chunkIndex + 1)}`;
            chunk.partNumber = chunkIndex + 1;

            chunks.push(chunk);

            start = end;
            chunkIndex += 1;
          }
        }

        return chunks;
      });


      const chunks = chunksPerFile.reduce((initialValue, fileChunks) => initialValue.concat(fileChunks), []);
      const zipFile = files.find(file => file.name.includes('.zip'));
      const sqlFile = files.find(file => file.name.includes('.sql'));


      yield put(sitesActions.wordpressFileMigration.request({
        hostingId,
        domain,
        migrationId,
        session,
        path,
        serverId,
        chunks,
        index: 0,
        hostname,
        migrationToken: `cpanel ${username}:${migrationToken.hash}`,
        baseAttempts,
        leftTrys: baseAttempts,
        zipFile,
        sqlFile,
      }));
    }

    yield put(sitesActions.wordpressFileMigration.successfullyRequestedData());
  } catch (error) {
    logger.error('Error on request wordpress migrate file data via files', error);
    yield put(sitesActions.wordpressFileMigration.loading(false));
    yield put(sitesActions.wordpressFileMigration.failureRequestedData());
    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
}

export function* requestWpFilesMigration({ payload }) {
  const {
    hostingId,
    domain,
    migrationId,
    session,
    path,
    serverId,
    chunks,
    index,
    leftTrys,
    hostname,
    migrationToken,
    baseAttempts,
    zipFile,
    sqlFile,
  } = payload;

  const totalChunks = chunks.length;
  const formData = new FormData();
  formData.append('dir', path);

  if (!chunks[index].partNumber) {
    formData.append('file', chunks[index]);
  }

  if (chunks[index].partNumber) {
    formData.append('partNumber', chunks[index].partNumber);
    formData.append('file', chunks[index], `${chunks[index].name}`);
  }

  const request = {
    headers: {
      Authorization: `${migrationToken}`,
      'x-server': `${hostname}:2083`,
    },
    data: formData,
    url: `${session}/execute/Fileman/upload_files`,
  };

  try {
    const response = yield call(api.cpanelProxy.post, request);

    if (response) {
      if (index === totalChunks - 1) {
        yield put(sitesActions.wordpressFileMigration.successfullyRequestedMigration());
        yield put(sitesActions.wordpressFileMigration.install({
          hostingId,
          migrationId,
          zipFile,
          sqlFile,
          path,
        }));
      }

      if (index < totalChunks - 1) {
        yield put(sitesActions.wordpressFileMigration.request({
          hostingId,
          domain,
          migrationId,
          session,
          path,
          serverId,
          chunks,
          hostname,
          migrationToken,
          baseAttempts,
          leftTrys: baseAttempts,
          index: (index + 1),
          zipFile,
          sqlFile,
        }));
      }
    }
  } catch (error) {
    const fileType = chunks[index].name.split('.').pop();
    logger.error('Error on migrate wordpress via files', error);

    const shouldRetry = (leftTrys - 1) > 0;

    if (shouldRetry) {
      yield put(sitesActions.wordpressFileMigration.request({
        hostingId,
        domain,
        migrationId,
        session,
        path,
        serverId,
        chunks,
        hostname,
        index,
        migrationToken,
        baseAttempts,
        leftTrys: (leftTrys - 1),
        zipFile,
        sqlFile,
      }));
    }

    if (!shouldRetry) {
      yield put(
        commonActions.notifications.set({
          toast: {
            showIcon: true,
            title: locale('onboardingHandler.filesMigration.migrationError.title')(fileType),
            info: locale('onboardingHandler.filesMigration.migrationError.description'),
            context: 'error',
          },
          maxWidth: '460px',
        }),
      );
      yield put(sitesActions.wordpressFileMigration.loading(false));
      yield put(sitesActions.wordpressFileMigration.failureRequestedMigration());
    }
  }
}

export function* requestWpFilesInstallation({ payload }) {
  const {
    hostingId, migrationId, zipFile, sqlFile, path,
  } = payload;
  const { token } = yield select(state => state.auth);

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      files: [
        {
          path,
          filename: zipFile.name,
          size: zipFile.size,
        },
        {
          path,
          filename: sqlFile.name,
          size: sqlFile.size,
        },
      ],
    },
    url: `api/v2/clientarea/${COUNTRY}/user/hostings/${hostingId}/wordpress/import/${migrationId}/install`,
  };

  yield put(sitesActions.wordpressFileMigration.loading(true));
  try {
    yield call(api.whmcs.post, request);
    yield put(sitesActions.wordpressFileMigration.loading(false));
    yield put(sitesActions.wordpressFileMigration.finishedRequestedInstallation(true));
  } catch (error) {
    logger.error('Error on request wordpress migrate file data via files', error);
    yield put(sitesActions.wordpressFileMigration.loading(false));
    yield put(sitesActions.wordpressFileMigration.failureRequestedInstallation());
    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
}

export function* requestWordpresSso({ payload }) {
  const { token } = yield select(state => state.auth);

  const {
    hostingId, siteId, domain, callbackFunction, setBlockedWindowUrl,
  } = payload;

  let newWindow = null;
  let newWindowUrl = null;
  let newWindowWasBlocked = null;

  yield put(sitesActions.wordpressSso.loading(true));
  try {
    const request = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: `api/v2/clientarea/${COUNTRY}/user/hostings/${hostingId}/site/${siteId}/${domain}/sso`,
    };

    const response = yield call(api.whmcs.get, request);
    newWindowUrl = response.data.data.url;

    if (newWindowUrl) {
      newWindow = window.open(response.data.data.url, '_blank');
      newWindowWasBlocked = !newWindow || newWindow.closed || typeof newWindow.closed === 'undefined';

      if (newWindowWasBlocked) {
        setBlockedWindowUrl && setBlockedWindowUrl(newWindowUrl);
      } else {
        callbackFunction && callbackFunction();
      }
    }

    yield put(sitesActions.wordpressSso.set(response.data.data.url));
    yield put(sitesActions.wordpressSso.success());
  } catch (e) {
    newWindow = window.open(`https://${domain}/wp-admin`, '_blank');
    newWindowWasBlocked = !newWindow || newWindow.closed || typeof newWindow.closed === 'undefined';

    if (newWindowWasBlocked) {
      setBlockedWindowUrl && setBlockedWindowUrl(newWindowUrl);
    } else {
      callbackFunction && callbackFunction();
    }
    yield put(sitesActions.wordpressSso.failure());
  } finally {
    yield put(sitesActions.wordpressSso.loading(false));
  }
}

export function* getSurveyQuestionsAnswered({ payload }) {
  const { SURVEY_API } = config;
  const { jwt: jwtToken } = yield select(state => state.auth);
  const { id: userId } = yield select(state => state.summary);
  const { surveyId } = payload;
  yield put(sitesActions.loading.set(true));
  try {
    const request = {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        'X-Client-ID': userId,
        'X-Brand': COUNTRY,
      },
      url: `${SURVEY_API}/users/${userId}/surveys/${surveyId}`,
    };

    const response = yield call(api.aws.get, request);
    const surveyAnswersData = sitesAssembly.receiveSurveyQuestionsAnswered(response.data[0]);
    yield put(sitesActions.surveyQuestionsAnswered.set(surveyAnswersData));
    yield put(sitesActions.surveyQuestionsAnswered.success());
    yield put(sitesActions.loading.set(false));
  } catch (e) {
    yield put(sitesActions.surveyQuestionsAnswered.failure());
    yield put(sitesActions.loading.set(false));
  }
}

export function* requestAddonCheck({ payload }) {
  const brand = isBRServer ? 'br' : 'es';
  const { token } = yield select(state => state.auth);
  const { hostingId } = payload;
  yield put(sitesActions.loading.set(true));
  try {
    const request = {
      headers: {
        Authorization: token,
      },
      url: `api/v2/clientarea/${brand}/user/hostings/${hostingId}/addon_check`,
    };

    const response = yield call(api.whmcs.get, request);
    yield put(sitesActions.addonCheck.success(response.data));
  } catch (e) {
    yield put(sitesActions.addonCheck.failure());
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('sitesWizard.wordPressInstall.errorToast')}`}
        </p>
      ),
      type: 'error',
    }));
    if (e.response && e.response.status === 401) {
      yield put(authActions.tokens.jwt.errorDuringRefresh(true));
    }
  }
  yield put(sitesActions.loading.set(false));
}

export function* requestVerifyIfSiteExist({ payload }) {
  const { token } = yield select(state => state.auth);
  const { hostingId, domain } = payload;
  const brand = isBRServer ? 'br' : 'es';
  yield put(sitesActions.verifyIfSiteExist.loading(true));
  try {
    const request = {
      headers: {
        Authorization: token,
      },
      url: `api/v2/clientarea/${brand}/user/hostings/site/${hostingId}/${domain}/check`,
    };

    const response = yield call(api.whmcs.get, request);
    const existSiteInfo = sitesAssembly.receiveSiteExistStatus(response.data.data);
    if (existSiteInfo.onlyDefaultFiles) {
      yield put(sitesActions.verifyIfSiteExist.defaultFilesExclude.request(existSiteInfo));
    } else {
      yield put(sitesActions.verifyIfSiteExist.success(existSiteInfo));
    }
    yield put(sitesActions.verifyIfSiteExist.loading(false));
  } catch (e) {
    yield put(sitesActions.verifyIfSiteExist.loading(false));
    yield put(sitesActions.verifyIfSiteExist.failure());
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('sitesWizard.wordPressInstall.errorToast')}`}
        </p>
      ),
      type: 'error',
    }));
    if (e.response && e.response.status === 401) {
      yield put(authActions.tokens.jwt.errorDuringRefresh(true));
    }
  }
}
export function* uninstallSite({ payload }) {
  const { token } = yield select(state => state.auth);

  const {
    hostingId, domain, domainHasPlan, onSuccess,
  } = payload;

  const brand = isBRServer ? 'br' : 'es';
  const clientareaBaseUrl = `api/v2/clientarea/${brand}/user/hostings`;
  const uninstallUrl = `${clientareaBaseUrl}/${hostingId}/domains/${domain}/site/uninstall`;
  const headers = {
    Authorization: token,
  };

  const request = {
    headers,
    url: uninstallUrl,
  };

  yield put(sitesActions.uninstallSite.loading(true));
  try {
    if (!domainHasPlan) {
      yield put(sitesActions.aditionalDomain.request(
        domain,
        parseInt(hostingId, 10),
      ));
    }

    yield call(api.whmcs.useDelete, request);
    yield put(sitesActions.uninstallSite.success());
    if (onSuccess) {
      onSuccess();
    }
  } catch (e) {
    logger.error('Error on uninstalling site files', e);

    yield put(sitesActions.uninstallSite.failure());
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('sitesWizard.wordPressInstall.errorToast')}`}
        </p>
      ),
      type: 'error',
    }));
  }
  yield put(sitesActions.uninstallSite.loading(false));
}

export function* requestDomainDnsStatusCheck({ payload }) {
  const brand = isBRServer ? 'br' : 'es';
  const { token } = yield select(state => state.auth);
  const { domain } = payload;
  const { jwt: jwtToken } = yield select(state => state.auth);
  try {
    const request = {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Service-Token': jwtToken,
      },
      url: `api/v2/clientarea/${brand}/domains/propagation`,
    };
    const response = yield call(api.whmcs.get, request);

    yield put(sitesActions.domainDnsStatusCheck.success(sitesAssembly.receiveChoosenDomainStatus(response.data.data, domain)));
  } catch (e) {
    yield put(sitesActions.domainDnsStatusCheck.failure());
  }
}

export function* requestDomainsDnsStatusCheck() {
  const brand = isBRServer ? 'br' : 'es';
  const { token } = yield select(state => state.auth);
  const { jwt: jwtToken } = yield select(state => state.auth);
  yield put(sitesActions.allDomainsDnsStatusCheck.loading(true));
  try {
    const request = {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Service-Token': jwtToken,
      },
      url: `api/v2/clientarea/${brand}/domains/propagation`,
    };
    const response = yield call(api.whmcs.get, request);
    yield put(sitesActions.allDomainsDnsStatusCheck.success(response.data.data));
  } catch (e) {
    yield put(sitesActions.allDomainsDnsStatusCheck.failure());
  }
  yield put(sitesActions.allDomainsDnsStatusCheck.loading(false));
}


export function* requestAutoSSLStart({ payload }) {
  const { token } = yield select(state => state.auth);
  const { domain, hostingId } = payload;
  const { jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/start-autossl/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.aws.post, request);
    if (response.data.success) {
      yield put(sitesActions.ssl.start.success());
    } else {
      yield put(sitesActions.ssl.start.failure());
    }
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas requestAutoSSLStart', error);
    yield put(sitesActions.ssl.start.failure());
  }
}

export function* requestAutoSSLProgress({ payload }) {
  const { token } = yield select(state => state.auth);
  const { domain, hostingId } = payload;
  const { jwt: jwtToken } = yield select(state => state.auth);
  const { ssl } = yield select(state => state.sites);
  const brand = isBRServer ? 'br' : 'es';
  const lastLoop = ssl.callLoop === 2;

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/check-autossl-progress`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.aws.get, request);

    if (!lastLoop && response.data.in_progress) {
      yield put(sitesActions.ssl.progress.loop({ callLoop: ssl.callLoop += 1 }));
    }

    if (lastLoop && response.data.in_progress) {
      yield put(sitesActions.ssl.progress.failure());
    }

    if (!response.data.in_progress) {
      yield put(sitesActions.ssl.check.request({ domain, hostingId }));
    }
  } catch (e) {
    yield put(sitesActions.ssl.progress.failure());
  }
}

export function* requestAutoSSLCheck({ payload }) {
  const { token } = yield select(state => state.auth);
  const { hostingId, domain } = payload;
  const { jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/ssl-status/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  try {
    const response = yield call(api.aws.get, request);

    if (response.data && response.data.status === 'active') {
      yield put(sitesActions.ssl.check.success());
    }

    if (response.data && (response.data.status === 'activation_failed' || response.data.status === 'not_installed')) {
      yield put(sitesActions.ssl.check.failure());
    }
  } catch (e) {
    yield put(sitesActions.ssl.check.failure());
  }
}

export function* requestSslVerification({ payload }) {
  const { token } = yield select(state => state.auth);
  const { domain, hostingId } = payload;
  const { jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/hosting/${hostingId}/ssl-pending-check/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  yield put(sitesActions.ssl.verification.loading(true));
  try {
    const response = yield call(api.aws.get, request);
    if (response.data) {
      yield put(sitesActions.ssl.verification.set(response.data && response.data.status));
    }
  } catch (e) {
    yield put(sitesActions.ssl.verification.set(AUTO_SSL_STATUS.CAN_INSTALL_SSL));
  }
  yield put(sitesActions.ssl.verification.loading(false));
}

export function* postSslDomainConfiguration({ payload }) {
  const { token } = yield select(state => state.auth);
  const { domain } = payload;
  const { jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    url: `${AWS_GATOR_SOLUTIONS_URL}/ssl/domain_configuration/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-region': brand,
      'x-service-token': jwtToken,
    },
  };

  yield put(sitesActions.ssl.pointDomain.loading(true));
  try {
    const response = yield call(api.aws.post, request);
    if (response.data.success) {
      yield put(sitesActions.ssl.pointDomain.success());
    } else {
      yield put(sitesActions.ssl.pointDomain.failure());
    }
  } catch (e) {
    yield put(sitesActions.ssl.pointDomain.failure());
  }
  yield put(sitesActions.ssl.pointDomain.loading(false));
}

export function* hasWelcomeChecklistInProgress({ payload }) {
  const { token } = yield select(state => state.auth);
  const { clientId, action } = payload;
  const brand = isBRServer ? 'br' : 'es';
  try {
    const request = {
      headers: {
        Authorization: token,
      },
      url: `api/v2/clientarea/${brand}/welcome-checklist?client_id=${clientId}`,
    };
    const response = yield call(api.whmcs.get, request);
    if (response && response.data && response.data.data) {
      yield put(sitesActions.welcomeChecklist.set(response.data.data));
      action && action(true);
      yield put(sitesActions.welcomeChecklist.success());
    }
  } catch (e) {
    yield put(sitesActions.welcomeChecklist.failure());
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('sitesWizard.wordPressInstall.errorToast')}`}
        </p>
      ),
      type: 'error',
    }));
  }
}

export function* updateStepWelcomeChecklist({ payload }) {
  const { token } = yield select(state => state.auth);

  const {
    clientId,
    hostingId,
    step,
    check,
  } = payload;

  const brand = isBRServer ? 'br' : 'es';
  try {
    const request = {
      data: {
        step,
        hostingId,
        userId: clientId,
        check,
      },
      headers: {
        Authorization: token,
      },
      url: `api/v2/clientarea/${brand}/welcome-checklist/`,
    };
    const response = yield call(api.whmcs.post, request);
    if (response && response.data && response.data.data) {
      yield put(sitesActions.welcomeChecklist.success());
    }
  } catch (e) {
    yield put(sitesActions.welcomeChecklist.failure());
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('sitesWizard.wordPressInstall.errorToast')}`}
        </p>
      ),
      type: 'error',
    }));
  }
}

export function* requestCreateRdLead({ payload }) {
  const { token } = yield select(state => state.auth);
  const {
    plan, domain,
  } = payload;

  const { name, phoneNumber, email } = yield select(state => state.summary);

  try {
    const request = {
      headers: {
        Authorization: token,
      },
      data: {
        payload: {
          name,
          email,
          personal_phone: phoneNumber,
          cf_hg_wiz_criar_site_dominio_apontamento: domain,
          cf_hg_wiz_criar_site_plano_selecionado: plan,
          conversion_identifier: 'hg-wizard-criar-site',
        },
        event_type: 'CONVERSION',
        event_family: 'CDP',
      },
      url: `/platform/conversions?api_key=${config.ECOMMERCE_RD_TOKEN}`,
    };

    yield call(api.rdStation.post, request);
  } catch (e) {
    logger.error('Error create RD lead', e);
  }
}

export function* requestGatorResolveForm({ payload }) {
  const {
    domain, referer, ticket, message, recaptcha,
  } = payload;
  const { name, email } = yield select(state => state.summary);

  try {
    const request = {
      data: {
        domain,
        referer,
        ticket,
        message,
        email,
        name,
        recaptcha,
      },
      url: `${WHMCS_URL}/api/v2/website/clients/contacts/complaint`,
    };

    yield put(sitesActions.gatorResolve.loading(true));
    yield call(api.external.post, request);
    yield put(sitesActions.gatorResolve.success());
    yield put(sitesActions.gatorResolve.loading(false));
  } catch (e) {
    yield put(sitesActions.gatorResolve.loading(false));
    logger.error('Error calling API', e);
    yield put(commonActions.notifications.set({
      label: (<p>{locale('sites.exclusionToast.failure')}</p>),
      type: 'error',
    }));
    yield put(sitesActions.gatorResolve.failure());
  }
}

function* requestCreateBusinessProfile({ payload }) {
  const { token } = yield select(state => state.auth);
  const userId = yield select(state => state.summary.id);

  const { jwt: jwtToken } = yield select(state => state.auth);

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    data: {
      name: payload.name,
      business_segment: payload.businessSegment,
      description: payload.description,
      phone: payload.phone,
      email: payload.email,
      tone_of_voice: payload.toneOfVoice,
      user_id: userId,
    },
    url: 'api/v3/business-profile',
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(sitesActions.businessProfile.success());
  } catch (e) {
    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );

    yield put(sitesActions.businessProfile.failure());
  }
}

function* requestCreateSiteWithBuilder({ payload }) {
  const { token } = yield select(state => state.auth);
  const {
    builderAddonId, domain, type, projectData, templateId, popupBlockedAction, isAgentFactory, aiBuilderAgentData,
  } = payload;

  const { jwt: jwtToken } = yield select(state => state.auth);

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    data: {
      builder_addon_id: builderAddonId,
      domain,
      type,
      ...(projectData && { project: projectData }),
      ...(templateId && { template_id: templateId }),
    },
    url: 'api/v3/builder/project',
  };

  if (!builderAddonId || !domain || !type) {
    const missingFields = [];
    if (!builderAddonId) missingFields.push('builderAddonId');
    if (!domain) missingFields.push('domain');
    if (!type) missingFields.push('type');

    logger.error('Missing required fields for creating site with builder:', missingFields);

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
    return;
  }

  yield put(sitesActions.sitebuilder.createSite.setLoading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    const ssoUrl = response.data.url || null;

    if (!isAgentFactory) {
      const newWindow = window.open(ssoUrl, '_blank');

      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        popupBlockedAction && popupBlockedAction();
      }
    }

    if (isAgentFactory && aiBuilderAgentData) {
      try {
        // Step 0: Create business profile first
        yield call(requestCreateBusinessProfile, {
          payload: {
            name: aiBuilderAgentData.name,
            businessSegment: aiBuilderAgentData.segment,
            description: aiBuilderAgentData.description,
            phone: aiBuilderAgentData.phone,
            email: aiBuilderAgentData.email,
            toneOfVoice: aiBuilderAgentData.toneOfVoice,
          },
        });

        logger.info('Business profile created successfully');

        // Step 1: Create agent and wait for response
        const createAgentResponse = yield call(createAgent, {
          payload: {
            name: aiBuilderAgentData.name,
            tone_of_voice: aiBuilderAgentData.toneOfVoice,
            system_objective: aiBuilderAgentData.description,
            position: 'bottom_right',
            color: '#0070D1',
          },
        });

        const agentId = createAgentResponse && createAgentResponse.agent_id;
        logger.info('Created agentId:', agentId);

        if (!agentId) {
          logger.error('Failed to create agent: no agent_id returned');
          return;
        }

        // Step 2: Activate agent and wait for response
        const activateAgentResponse = yield call(activateAgent, {
          domain,
          agentId,
        });

        logger.info('Activated agent response:', activateAgentResponse);

        const script = activateAgentResponse && activateAgentResponse.script;
        logger.info('Agent script to install:', script);

        // Step 3: Install AI agent
        yield call(installAiAgent, {
          payload: {
            domain,
            builder_addon_id: builderAddonId,
            script,
          },
        });

        logger.info('Agent Factory setup completed successfully');
      } catch (error) {
        logger.error('Error setting up Agent Factory:', error);
        yield put(
          commonActions.notifications.set({
            toast: {
              showIcon: true,
              title: locale('onboardingHandler.genericErrorTitle'),
              info: locale('onboardingHandler.genericErrorInfo'),
              context: 'error',
            },
            maxWidth: '460px',
          }),
        );
      }
    }

    yield put(sitesActions.sitebuilder.createSite.set({ builderAddonId, domain, url: ssoUrl }));
    yield put(sitesActions.sitebuilder.createSite.setLoading(false));
  } catch (e) {
    yield put(sitesActions.sitebuilder.createSite.setLoading(false));

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );

    yield put(sitesActions.sitebuilder.createSite.failure());
  }
}

function* requestCreatePreviewWithBuilder({ payload }) {
  const { token } = yield select(state => state.auth);
  const {
    type, projectData,
  } = payload;

  const { jwt: jwtToken } = yield select(state => state.auth);

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    data: {
      type,
      ...projectData,
    },
    url: 'api/v3/builder/project/preview',
  };

  if (!type) {
    const missingFields = [];
    if (!type) missingFields.push('type');

    logger.error('Missing required fields for creating site with builder:', missingFields);

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
    return;
  }

  yield put(sitesActions.sitebuilder.createPreview.setLoading(true));

  try {
    const response = yield call(api.whmcs.post, request);

    yield put(sitesActions.sitebuilder.createPreview.set({ projectUid: response.data.project_uid || null }));
    yield put(sitesActions.sitebuilder.createPreview.setLoading(false));
  } catch (e) {
    yield put(sitesActions.sitebuilder.createPreview.setLoading(false));

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );

    yield put(sitesActions.sitebuilder.createPreview.failure());
  }
}

function* requestSyncPreviewWithBuilder({ payload }) {
  const { token } = yield select(state => state.auth);
  const {
    builderAddonId, domain, isAgentFactory, aiBuilderAgentData,
  } = payload;

  const { jwt: jwtToken } = yield select(state => state.auth);
  const projectUid = yield select(state => state.sites.sitebuilder.createPreview.projectUid);

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    data: {
      builder_addon_id: builderAddonId,
      domain,
      type: 'ai-project-preview-sync',
      project_uid: projectUid,
    },
    url: 'api/v3/builder/project',
  };

  if (!builderAddonId || !domain) {
    const missingFields = [];
    if (!builderAddonId) missingFields.push('builderAddonId');
    if (!domain) missingFields.push('domain');

    logger.error('Missing required fields for creating site with builder:', missingFields);

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
    return;
  }

  yield put(sitesActions.sitebuilder.createSite.setLoading(true));

  try {
    const response = yield call(api.whmcs.post, request);
    const ssoUrl = response.data.url || null;

    if (isAgentFactory && aiBuilderAgentData) {
      try {
        // Step 0: Create business profile first
        yield call(requestCreateBusinessProfile, {
          payload: {
            name: aiBuilderAgentData.name,
            businessSegment: aiBuilderAgentData.segment,
            description: aiBuilderAgentData.description,
            phone: aiBuilderAgentData.phone,
            email: aiBuilderAgentData.email,
            toneOfVoice: aiBuilderAgentData.toneOfVoice,
          },
        });

        logger.info('Business profile created successfully');

        // Step 1: Create agent and wait for response
        const createAgentResponse = yield call(createAgent, {
          payload: {
            name: aiBuilderAgentData.name,
            tone_of_voice: aiBuilderAgentData.toneOfVoice,
            system_objective: aiBuilderAgentData.description,
            position: 'bottom_right',
            color: '#0070D1',
          },
        });

        const agentId = createAgentResponse && createAgentResponse.agent_id;
        logger.info('Created agentId:', agentId);

        if (!agentId) {
          logger.error('Failed to create agent: no agent_id returned');
          return;
        }

        // Step 2: Activate agent and wait for response
        const activateAgentResponse = yield call(activateAgent, {
          domain,
          agentId,
        });

        logger.info('Activated agent response:', activateAgentResponse);

        const script = activateAgentResponse && activateAgentResponse.script;
        logger.info('Agent script to install:', script);

        // Step 3: Install AI agent
        yield call(installAiAgent, {
          payload: {
            domain,
            builder_addon_id: builderAddonId,
            script,
          },
        });

        logger.info('Agent Factory setup completed successfully');
      } catch (error) {
        logger.error('Error setting up Agent Factory:', error);
        yield put(
          commonActions.notifications.set({
            toast: {
              showIcon: true,
              title: locale('onboardingHandler.genericErrorTitle'),
              info: locale('onboardingHandler.genericErrorInfo'),
              context: 'error',
            },
            maxWidth: '460px',
          }),
        );
      }
    }

    yield put(sitesActions.sitebuilder.createSite.set({ builderAddonId, domain, url: ssoUrl }));
    yield put(sitesActions.sitebuilder.createSite.setLoading(false));
  } catch (e) {
    yield put(sitesActions.sitebuilder.createSite.setLoading(false));

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );

    yield put(sitesActions.sitebuilder.createSite.failure());
  }
}

function* getAgentsFactoryCheckAvailability() {
  const { id: clientId } = yield select(state => state.summary);
  const agentFactoryAuth = JSON.parse(getCookie('agent-factory-auth'));
  const token = agentFactoryAuth.access_token;

  const request = {
    url: `${AGENT_FACTORY_ORCHESTRATOR_API_URL}/user/eligibility`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': clientId,
      'X-Brand': COUNTRY,
    },
  };

  yield put(sitesActions.agentsFactory.loading(true));

  try {
    const response = yield call(api.external.get, request);
    yield put(sitesActions.agentsFactory.account.success());
    yield put(sitesActions.agentsFactory.account.set({ agentsFactory: response.data.data }));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas agentsFactory - method getAgentsFactoryCheckAvailability', error);
    yield put(sitesActions.agentsFactory.account.failure());
  }

  yield put(sitesActions.agentsFactory.loading(false));
}

function* sagas() {
  yield takeLatest(SitesActionTypes.REQUEST_GET_SITES, getSitesRequest);
  yield takeLatest(SitesActionTypes.REQUEST_GET_TLDS, getTlds);
  yield takeLatest(SitesActionTypes.REQUEST_GET_DOMAIN_AVAILABILITY, getDomainAvailability);
  yield takeLatest(SitesActionTypes.REQUEST_GET_DOMAINS, getDomainsRequest);
  yield takeLatest(SitesActionTypes.DELETE_SITE, deleteSite);
  yield takeLatest(SitesActionTypes.SET_WARNING_SEEN, setWarningSeen);
  yield takeLatest(SitesActionTypes.REQUEST_WORDPRESS_INSTALLATION, requestWordPressInstallation);
  yield takeLatest(SitesActionTypes.REQUEST_WORDPRESS_FILES_DATA, requestWpMigrationFileData);
  yield takeLatest(SitesActionTypes.REQUEST_WORDPRESS_FTP_MIGRATION, requestWordpressFtpInstallation);
  yield takeLatest(SitesActionTypes.REQUEST_WORDPRESS_FILES_MIGRATION, requestWpFilesMigration);
  yield takeLatest(SitesActionTypes.WORDPRESS_INSTALL_FILES, requestWpFilesInstallation);
  yield takeLatest(SitesActionTypes.REQUEST_WORDPRESS_SSO, requestWordpresSso);
  yield takeLatest(SitesActionTypes.REQUEST_GET_PLANS, getPlans);
  yield takeLatest(SitesActionTypes.REQUEST_ADITIONAL_DOMAIN_TO_PLAN, linkDomainToPlan);
  yield takeLatest(SitesActionTypes.REQUEST_CHANGE_DOMAIN_DNS, changeDomainDns);
  yield takeLatest(SitesActionTypes.REQUEST_DOMAIN_DNS, getDomainDns);
  yield takeLatest(SitesActionTypes.REQUEST_PLAN_DNS, getPlanDns);
  yield takeLatest(SitesActionTypes.REQUEST_SURVEY_QUESTIONS_ANSWERED, getSurveyQuestionsAnswered);
  yield takeLatest(SitesActionTypes.REQUEST_ADDON_CHECK, requestAddonCheck);
  yield takeLatest(SitesActionTypes.REQUEST_VERIFY_SITE_EXIST, requestVerifyIfSiteExist);
  yield takeLatest(SitesActionTypes.REQUEST_DOMAIN_DNS_STATUS_CHECK, requestDomainDnsStatusCheck);
  yield takeLatest(SitesActionTypes.AUTO_SSL_START_REQUEST, requestAutoSSLStart);
  yield takeLatest(SitesActionTypes.AUTO_SSL_PROGRESS_REQUEST, requestAutoSSLProgress);
  yield takeLatest(SitesActionTypes.AUTO_SSL_CHECK_REQUEST, requestAutoSSLCheck);
  yield takeLatest(SitesActionTypes.SSL_VERIFICATION_REQUEST, requestSslVerification);
  yield takeLatest(SitesActionTypes.REQUEST_POST_DOMAIN_TO_HG, postSslDomainConfiguration);
  yield takeLatest(SitesActionTypes.REQUEST_ALL_DOMAINS_DNS_STATUS_CHECK, requestDomainsDnsStatusCheck);
  yield takeLatest(SitesActionTypes.REQUEST_UNINSTALL_SITE, uninstallSite);
  yield takeLatest(SitesActionTypes.GET_STEP_WELCOME_CHECKLIST_REQUEST, hasWelcomeChecklistInProgress);
  yield takeLatest(SitesActionTypes.POST_STEP_WELCOME_CHECKLIST_REQUEST, updateStepWelcomeChecklist);
  yield takeLatest(SitesActionTypes.RD_CREATE_LEAD_REQUEST, requestCreateRdLead);
  yield takeLatest(SitesActionTypes.REDIRECT_TO_EDIT, redirectToEdit);
  yield takeLatest(SitesActionTypes.REQUEST_SITE_BUILDER_TOOLS, requestSiteBuilderTools);
  yield takeLatest(SitesActionTypes.REQUEST_GET_SITE_BUILDER_TOOLS, getSiteBuilderTools);
  yield takeLatest(SitesActionTypes.REQUEST_GATOR_RESOLVE_FORM, requestGatorResolveForm);
  yield takeLatest(SitesActionTypes.CREATE_SITE_WITH_BUILDER_REQUEST, requestCreateSiteWithBuilder);
  yield takeLatest(SitesActionTypes.CREATE_PREVIEW_WITH_BUILDER_REQUEST, requestCreatePreviewWithBuilder);
  yield takeLatest(SitesActionTypes.SYNC_PREVIEW_WITH_BUILDER_REQUEST, requestSyncPreviewWithBuilder);
  yield takeLatest(SitesActionTypes.REQUEST_BUILDER_SSO, requestBuilderSso);
  yield takeLatest(SitesActionTypes.AGENTS_FACTORY_CHECK_AVAILABILITY_REQUEST, getAgentsFactoryCheckAvailability);
  yield takeLatest(SitesActionTypes.CREATE_BUSINESS_PROFILE_REQUEST, requestCreateBusinessProfile);
}

export default sagas;
